import { useEffect, useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { LoadingState } from '@/components/shell/LoadingState';
import { payApiClient } from '@/api/client';
import type { Gateway, GatewayCredentialFieldWrite, GatewayCredentialValueEncoding } from '@/api/models';
import { useGatewayDescriptor } from './useGatewayDescriptors';

interface GatewayAccountSecretsWriteDialogProps {
  open: boolean;
  gatewayAccountId: string;
  /** The account's own gateway — decides which credential fields this dialog asks for. */
  gateway: Gateway;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * `PUT /v1/admin/gateway-accounts/{id}/secrets` — write-only (D-M). Every row
 * here starts empty: there is nothing to prefill a value from, since no
 * operation in this contract ever returns one. Submitting only ever sends the
 * rows the admin filled in; the platform decides per field whether it creates
 * the first version or rotates an existing one.
 *
 * The rows themselves come from the gateway's own descriptor
 * (`GET /v1/admin/gateways`), so an administrator is asked for the credentials
 * this gateway actually has, under names the platform will read back, and the
 * value encoding travels with the field instead of being a choice anyone has to
 * get right — a signing key stored as the text of its base64 rather than the
 * bytes it encodes produces an account that looks configured and fails at the
 * first payment.
 *
 * When the platform publishes no descriptor (an API older than that surface),
 * the dialog degrades to its previous free-form shape rather than blocking the
 * screen — an administrator who knows the field names must still be able to
 * work.
 */
export function GatewayAccountSecretsWriteDialog({ open, gatewayAccountId, gateway, onClose, onSaved }: GatewayAccountSecretsWriteDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const { descriptor, loading, unavailable } = useGatewayDescriptor(gateway, open);
  const [values, setValues] = useState<Record<string, string>>({});
  const [manualFields, setManualFields] = useState<GatewayCredentialFieldWrite[]>([{ fieldName: '', value: '', valueEncoding: 'utf8' }]);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setValues({});
      setManualFields([{ fieldName: '', value: '', valueEncoding: 'utf8' }]);
      setAccessDenied(false);
    }
  }, [open]);

  function updateManualField(index: number, patch: Partial<GatewayCredentialFieldWrite>) {
    setManualFields((prev) => prev.map((field, i) => (i === index ? { ...field, ...patch } : field)));
  }

  function removeManualField(index: number) {
    setManualFields((prev) => prev.filter((_, i) => i !== index));
  }

  function addManualField() {
    setManualFields((prev) => [...prev, { fieldName: '', value: '', valueEncoding: 'utf8' }]);
  }

  /** A field's human label, falling back to its own platform-side name when this console has no translation for it (a gateway added API-side first). */
  function fieldLabel(fieldName: string): string {
    return translate(`pay.gatewayAccounts.credentialFields.${gateway}.${fieldName}.label`, { _: fieldName });
  }

  function fieldHelp(fieldName: string): string | undefined {
    return translate(`pay.gatewayAccounts.credentialFields.${gateway}.${fieldName}.help`, { _: '' }) || undefined;
  }

  const guidedFields: GatewayCredentialFieldWrite[] = (descriptor?.credentialFields ?? [])
    .filter((field) => (values[field.fieldName] ?? '').length > 0)
    .map((field) => ({
      fieldName: field.fieldName,
      value: values[field.fieldName] ?? '',
      valueEncoding: field.valueEncoding,
    }));

  const manualValidFields = manualFields.filter((field) => field.fieldName.trim().length > 0 && field.value.length > 0);
  const manualHasInvalidRow = manualFields.some((field) => (field.fieldName.trim().length > 0) !== (field.value.length > 0));

  const fieldsToWrite = descriptor ? guidedFields : manualValidFields;
  const canSubmit = !submitting && fieldsToWrite.length > 0 && (descriptor ? true : !manualHasInvalidRow);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await payApiClient.writeAdminGatewayAccountSecrets(gatewayAccountId, { fields: fieldsToWrite });
      notify(translate('pay.gatewayAccounts.secretsWriteDialog.success'), { type: 'success' });
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('pay.gatewayAccounts.secretsWriteDialog.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : loading ? (
          <LoadingState variant="list" rows={2} />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Alert severity="warning">{translate('pay.gatewayAccounts.secretsWriteDialog.body')}</Alert>
            {descriptor ? (
              <Stack spacing={2}>
                {descriptor.credentialFields.map((field) => (
                  <TextField
                    key={field.fieldName}
                    size="small"
                    type="password"
                    autoComplete="off"
                    label={fieldLabel(field.fieldName)}
                    helperText={fieldHelp(field.fieldName)}
                    value={values[field.fieldName] ?? ''}
                    onChange={(event) => setValues((prev) => ({ ...prev, [field.fieldName]: event.target.value }))}
                    fullWidth
                  />
                ))}
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                {unavailable && <Alert severity="info">{translate('pay.gatewayAccounts.secretsWriteDialog.manualBody')}</Alert>}
                {manualFields.map((field, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      size="small"
                      label={translate('pay.gatewayAccounts.secretsWriteDialog.fieldName')}
                      helperText={translate('pay.gatewayAccounts.secretsWriteDialog.fieldNameHelp')}
                      value={field.fieldName}
                      onChange={(event) => updateManualField(index, { fieldName: event.target.value })}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      type="password"
                      autoComplete="off"
                      label={translate('pay.gatewayAccounts.secretsWriteDialog.fieldValue')}
                      value={field.value}
                      onChange={(event) => updateManualField(index, { value: event.target.value })}
                      fullWidth
                    />
                    <TextField
                      select
                      size="small"
                      label={translate('pay.gatewayAccounts.secretsWriteDialog.valueEncoding')}
                      helperText={translate('pay.gatewayAccounts.secretsWriteDialog.valueEncodingHelp')}
                      value={field.valueEncoding ?? 'utf8'}
                      onChange={(event) => updateManualField(index, { valueEncoding: event.target.value as GatewayCredentialValueEncoding })}
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="utf8">{translate('pay.gatewayAccounts.secretsWriteDialog.valueEncodingText')}</MenuItem>
                      <MenuItem value="base64">{translate('pay.gatewayAccounts.secretsWriteDialog.valueEncodingBase64')}</MenuItem>
                    </TextField>
                    <IconButton
                      size="small"
                      onClick={() => removeManualField(index)}
                      aria-label={translate('pay.gatewayAccounts.secretsWriteDialog.remove')}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button size="small" startIcon={<AddOutlinedIcon fontSize="small" />} onClick={addManualField} sx={{ alignSelf: 'flex-start' }}>
                  {translate('pay.gatewayAccounts.secretsWriteDialog.add')}
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.gatewayAccounts.secretsWriteDialog.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {translate('pay.gatewayAccounts.secretsWriteDialog.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
